import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, Minus, Plus, Search, X, ArrowRight, Lock } from 'lucide-react'
import { fetchPaidQuantities } from '../../../api/paymentsApi'
import PaymentSuccessScreen from './PaymentSuccessScreen'

// Payment modes
enum PaymentMode {
  FULL = 'full',
  SELECT_ITEMS = 'select',
  SPLIT_EQUALLY = 'split'
}

interface PaymentSectionProps {
  mesa: {
    mesaId: string
    restaurantId?: string
    numeroMesa: string
    items: Array<{
      id: string
      name: string
      price: number
      quantity: number
    }>
    montoTotal: number
    montoPagado: number
    restante: number
    moneda: string
    ticketId?: string
    /** Total with tax (for bill display). From API or computed from montoTotal + tax. */
    montoTotalConImpuestos?: number
    /** Restaurant tax rate (e.g. 18). Used when impuestosIncluded is false. */
    taxRate?: number
    /** If true, POS/montoTotal already includes tax; if false, YAP adds tax. */
    impuestosIncluded?: boolean
    /** Session/mesa estado ('abierta' | 'cerrada') when provided by backend. */
    estado?: string
  }
}

// Format currency helper with thousand separators
const formatCurrency = (amount: number, moneda: string = 'DOP') => {
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
  return `$${formatted} ${moneda}`
}

// Header Component
const Header: React.FC<{ title: string }> = ({ title }) => {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-50/80 border-b border-gray-200/50">
      <div className="max-w-md mx-auto px-4 h-12 flex items-center justify-center">
        <h1 className="text-sm font-semibold text-gray-900 tracking-tight">
          {title}
        </h1>
      </div>
    </header>
  )
}

// TabSelector Component
const TabSelector: React.FC<{
  currentMode: PaymentMode
  onModeChange: (mode: PaymentMode) => void
}> = ({ currentMode, onModeChange }) => {
  return (
    <div className="bg-gray-200/60 p-0.5 rounded-[1rem] flex gap-0.5 relative z-0">
      {[
        { mode: PaymentMode.FULL, label: 'Cuenta Completa' },
        { mode: PaymentMode.SELECT_ITEMS, label: 'Seleccionar Items' },
        { mode: PaymentMode.SPLIT_EQUALLY, label: 'Dividir Igual' },
      ].map((tab) => (
        <button
          key={tab.mode}
          onClick={() => onModeChange(tab.mode)}
          className={`flex-1 py-2 rounded-[0.875rem] text-[11px] font-semibold tracking-tight transition-all duration-300 relative z-10 ${
            currentMode === tab.mode
              ? 'bg-white text-gray-900 shadow-[0_1px_4px_rgba(0,0,0,0.06)]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  )
}

// SearchBar Component
const SearchBar: React.FC<{
  value: string
  onChange: (val: string) => void
}> = ({ value, onChange }) => {
  return (
    <div className="px-3 py-2 bg-white">
      <div className="relative group">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={14} />
        <input
          type="text"
          placeholder="Filtrar items..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-gray-100/70 text-gray-900 text-[13px] rounded-xl pl-9 pr-8 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:bg-white transition-all placeholder-gray-400 font-medium"
        />
        {value && (
          <button
            onClick={() => onChange('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 bg-gray-200/50 p-0.5 rounded-full transition-colors"
          >
            <X size={10} strokeWidth={3} />
          </button>
        )}
      </div>
    </div>
  )
}

// ItemRow Component — shows subtotal and tax as separate lines when tax applies
const ItemRow: React.FC<{
  item: { id: string; name: string; price: number; quantity: number }
  isSelected: boolean
  mode: PaymentMode
  onToggle: (id: string) => void
  isPaid: boolean
  moneda: string
  taxRate?: number
  impuestosIncluded?: boolean
}> = ({ item, isSelected, mode, onToggle, isPaid, moneda, taxRate = 18, impuestosIncluded = true }) => {
  const isSelectMode = mode === PaymentMode.SELECT_ITEMS
  const subtotal = item.price * item.quantity
  const taxAmount = impuestosIncluded ? 0 : subtotal * (taxRate / 100)
  const unitPrice = item.price

  return (
    <div
      onClick={() => isSelectMode && !isPaid && onToggle(item.id)}
      className={`group flex flex-col px-3 py-2.5 transition-all duration-200 cursor-pointer relative overflow-hidden
        ${isSelectMode && !isPaid ? 'active:scale-[0.99]' : ''}
        ${isSelectMode && isSelected ? 'bg-blue-50/50' : 'bg-transparent'}
        ${isPaid ? 'opacity-60' : ''}
      `}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`shrink-0 ${isSelectMode ? 'w-5 mr-0.5' : 'w-0 -ml-2 overflow-hidden'}`}>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
              ${isPaid ? 'border-gray-300 bg-gray-200 cursor-not-allowed' : ''}
              ${isSelected && !isPaid ? 'bg-gray-900 border-gray-900' : 'border-gray-300 bg-transparent'}
            `}>
              {isSelected && !isPaid && <Check size={10} className="text-white" strokeWidth={3} />}
            </div>
          </div>
          <div className="flex flex-col min-w-0">
              <span className={`text-[13px] font-medium leading-tight ${
                isSelected && isSelectMode && !isPaid ? 'text-gray-900' : 'text-gray-700'
              }`}>
                {item.name}
                {isPaid && <span className="ml-1.5 text-[10px] text-green-600">(Pagado)</span>}
              </span>
              {item.quantity > 1 && (
                <span className="text-[10px] text-gray-400 font-medium mt-0.5">
                  {formatCurrency(unitPrice, moneda)} cada uno
                </span>
              )}
          </div>
        </div>
        <div className={`font-semibold text-gray-900 tabular-nums text-[13px] shrink-0 ${
          isSelected && isSelectMode && !isPaid ? 'text-blue-600' : ''
        }`}>
          {formatCurrency(subtotal, moneda)}
        </div>
      </div>
      {!impuestosIncluded && taxAmount > 0 && (
        <div className="flex items-center justify-between mt-0.5">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className={`shrink-0 ${isSelectMode ? 'w-5 mr-0.5' : 'w-0 -ml-2 overflow-hidden'}`} />
            <span className="text-[9px] text-gray-400">Impuesto ({taxRate}%)</span>
          </div>
          <span className="text-[9px] text-gray-400 tabular-nums shrink-0">
            {formatCurrency(taxAmount, moneda)}
          </span>
        </div>
      )}
    </div>
  )
}

// SummarySection Component — shows Subtotal, Impuesto (separate), then Total
const SummarySection: React.FC<{
  subtotal: number
  taxAmount: number
  total: number
  paid: number
  remaining: number
  selectedSubtotal?: number
  selectedTaxAmount?: number
  selectedTotal?: number
  showSelectionTotal: boolean
  moneda: string
  showTaxBreakdown: boolean
}> = ({
  subtotal,
  taxAmount,
  total,
  paid,
  remaining,
  selectedSubtotal = 0,
  selectedTaxAmount = 0,
  selectedTotal = 0,
  showSelectionTotal,
  moneda,
  showTaxBreakdown,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100/50 p-3 relative overflow-hidden">
      <div className="relative z-10 space-y-2">
        {showSelectionTotal ? (
          <div className="flex flex-col gap-1">
            {showTaxBreakdown && selectedTaxAmount > 0 ? (
              <>
                <div className="flex justify-between text-[10px]">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-gray-700">{formatCurrency(selectedSubtotal, moneda)}</span>
                </div>
                <div className="flex justify-between text-[9px]">
                  <span className="text-gray-400">Impuesto</span>
                  <span className="text-gray-400">{formatCurrency(selectedTaxAmount, moneda)}</span>
                </div>
              </>
            ) : null}
            <div className="flex flex-col items-center justify-center">
              <span className="text-[10px] font-medium text-gray-400 mb-0.5">Monto Seleccionado</span>
              <span className="text-lg font-bold text-gray-900 tracking-tight">
                {formatCurrency(selectedTotal, moneda)}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {showTaxBreakdown && taxAmount > 0 ? (
              <>
                <div className="flex justify-between text-[10px]">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-gray-700">{formatCurrency(subtotal, moneda)}</span>
                </div>
                <div className="flex justify-between text-[9px]">
                  <span className="text-gray-400">Impuesto</span>
                  <span className="text-gray-400">{formatCurrency(taxAmount, moneda)}</span>
                </div>
              </>
            ) : null}
            <div className="flex flex-col items-center justify-center">
              <span className="text-[10px] font-medium text-gray-400 mb-0.5">Total de la Cuenta</span>
              <span className="text-lg font-bold text-gray-900 tracking-tight">
                {formatCurrency(total, moneda)}
              </span>
            </div>
          </div>
        )}

        {paid > 0 && (
          <div className="flex justify-between items-center bg-green-50/50 p-1.5 rounded-lg border border-green-100/50">
            <span className="text-green-700 text-[10px] font-medium flex items-center gap-1">
              <Check size={10} className="bg-green-200 text-green-700 rounded-full p-0.5" />
              Pagado
            </span>
            <span className="font-semibold text-green-700 text-[10px]">{formatCurrency(paid, moneda)}</span>
          </div>
        )}

        <div className="flex justify-between items-center text-[10px] pt-1 border-t border-gray-100">
          <span className="text-gray-400">Restante</span>
          <span className="text-gray-900 font-semibold">{formatCurrency(remaining, moneda)}</span>
        </div>
      </div>
    </div>
  )
}

// SplitEqualControl Component
const SplitEqualControl: React.FC<{
  totalAmount: number
  splitCount: number
  onCountChange: (count: number) => void
  moneda: string
}> = ({ totalAmount, splitCount, onCountChange, moneda }) => {
  const amountPerPerson = totalAmount / splitCount

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100/50 p-3 mb-3">
      <div className="text-center mb-4">
        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-0.5">Por Persona</p>
        <p className="text-xl font-bold text-gray-900 tracking-tighter">{formatCurrency(amountPerPerson, moneda)}</p>
      </div>

      <div className="flex items-center justify-between bg-gray-50/80 rounded-lg p-1 border border-gray-100">
        <button 
          onClick={() => onCountChange(Math.max(1, splitCount - 1))}
          className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] text-gray-600 hover:text-black active:scale-90 transition-all"
        >
          <Minus size={18} />
        </button>
        
        <div className="flex flex-col items-center w-16">
          <span className="text-lg font-bold text-gray-900 tabular-nums leading-none">{splitCount}</span>
          <span className="text-[8px] uppercase font-bold text-gray-400 mt-0.5 tracking-wider">Personas</span>
        </div>

        <button 
          onClick={() => onCountChange(splitCount + 1)}
          className="w-10 h-10 flex items-center justify-center bg-white rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] text-gray-600 hover:text-black active:scale-90 transition-all"
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  )
}

/**
 * Enhanced Payment Section with exact design match
 * Supports: Full bill, Select items, Split equally
 */
export function PaymentSection({ mesa }: PaymentSectionProps) {
  const navigate = useNavigate()
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(PaymentMode.FULL)
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set())
  const [splitCount, setSplitCount] = useState(2)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paidQuantities, setPaidQuantities] = useState<Map<string, number>>(new Map())
  const [searchQuery, setSearchQuery] = useState('')

  // Load paid quantities
  React.useEffect(() => {
    const ticketId = mesa.ticketId || mesa.mesaId
    if (ticketId) {
      fetchPaidQuantities(ticketId)
        .then(setPaidQuantities)
        .catch(err => {
          console.error('Failed to fetch paid quantities:', err)
        })
    }
  }, [mesa.ticketId, mesa.mesaId])

  // Check if item is fully paid
  const isItemPaid = (item: { id: string; quantity: number }) => {
    const paidQty = paidQuantities.get(item.id) || 0
    return paidQty >= item.quantity
  }

  // Filter out paid items for selection
  const availableItems = useMemo(() => {
    return mesa.items.filter(item => !isItemPaid(item))
  }, [mesa.items, paidQuantities])

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery) return mesa.items
    return mesa.items.filter(item => 
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
  }, [mesa.items, searchQuery])

  // Tax settings from API (bill and items show totals with tax when impuestosIncluded is false)
  const taxRate = mesa.taxRate ?? 18
  const impuestosIncluded = mesa.impuestosIncluded === true
  const totalAmount =
    mesa.montoTotalConImpuestos ??
    (impuestosIncluded ? (mesa.montoTotal ?? 0) : (mesa.montoTotal ?? 0) * (1 + taxRate / 100))
  const paidAmount = mesa.montoPagado ?? 0
  const remainingAmount = Math.max(0, totalAmount - paidAmount)
  const isClosed = (mesa as any)?.estado === 'cerrada'
  const moneda = mesa.moneda || 'DOP'

  const billSubtotal = mesa.montoTotal ?? 0
  const billTaxAmount = impuestosIncluded ? 0 : Math.round((totalAmount - billSubtotal) * 100) / 100

  const { selectedSubtotal, selectedTaxAmount, currentPayableAmount } = useMemo(() => {
    const selSub = availableItems
      .filter(item => selectedItemIds.has(item.id))
      .reduce((sum, item) => sum + item.price * item.quantity, 0)
    const selTax = impuestosIncluded ? 0 : Math.round(selSub * (taxRate / 100) * 100) / 100
    const payable = impuestosIncluded ? selSub : selSub + selTax
    return {
      selectedSubtotal: selSub,
      selectedTaxAmount: selTax,
      currentPayableAmount:
        paymentMode === PaymentMode.FULL
          ? remainingAmount
          : paymentMode === PaymentMode.SPLIT_EQUALLY
            ? remainingAmount / splitCount
            : paymentMode === PaymentMode.SELECT_ITEMS
              ? payable
              : 0,
    }
  }, [paymentMode, remainingAmount, splitCount, availableItems, selectedItemIds, impuestosIncluded, taxRate])

  const handleToggleItem = (id: string) => {
    const newSet = new Set(selectedItemIds)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedItemIds(newSet)
  }

  const handlePayment = async () => {
    if (currentPayableAmount <= 0) return
    
    setIsProcessing(true)

    try {
      const ticketId = mesa.ticketId || mesa.mesaId
      
      // Build selected items based on mode
      let itemsToPay: Array<{ itemId: string; quantity: number }> = []
      
      if (paymentMode === PaymentMode.FULL) {
        itemsToPay = availableItems.map(item => ({
          itemId: item.id,
          quantity: item.quantity
        }))
      } else if (paymentMode === PaymentMode.SELECT_ITEMS) {
        itemsToPay = availableItems
          .filter(item => selectedItemIds.has(item.id))
          .map(item => ({
            itemId: item.id,
            quantity: item.quantity
          }))
      } else if (paymentMode === PaymentMode.SPLIT_EQUALLY) {
        itemsToPay = availableItems.map(item => ({
          itemId: item.id,
          quantity: item.quantity
        }))
      }

      if (itemsToPay.length === 0) {
        alert('No hay items seleccionados para pagar')
        setIsProcessing(false)
        return
      }

      // Amount to charge: with tax when restaurant has impuestosIncluded false
      const subtotal = itemsToPay.reduce((sum, itemRef) => {
        const item = mesa.items.find(i => i.id === itemRef.itemId)
        const line = item ? item.price * item.quantity : 0
        return sum + (mesa.impuestosIncluded ? line : line * (1 + (mesa.taxRate ?? 18) / 100))
      }, 0)

      navigate('/tip-selection', {
        state: {
          ticketId,
          mesaId: mesa.mesaId,
          restaurantId: mesa.restaurantId,
          waiterName: mesa.numeroMesa || 'tu mesero',
          amount: subtotal,
          selectedItems: itemsToPay,
          splitCount: paymentMode === PaymentMode.SPLIT_EQUALLY ? splitCount : undefined,
          paymentMode
        }
      })
    } catch (error: any) {
      console.error('Payment error:', error)
      alert(error.message || 'Error al procesar el pago')
      setIsProcessing(false)
    }
  }

  // If session is closed or fully paid from YAP's perspective, show success screen
  if (isClosed || remainingAmount <= 0) {
    return <PaymentSuccessScreen />
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <Header title={`Mesa ${mesa.numeroMesa}`} />

      <main className="max-w-md mx-auto px-4 py-3 space-y-3">
        {/* Payment Mode Selector */}
        <TabSelector 
          currentMode={paymentMode} 
          onModeChange={(mode) => {
            setPaymentMode(mode)
            setSelectedItemIds(new Set())
          }} 
        />

        {/* Dynamic Controls based on mode (Split Equal shows on top) */}
        {paymentMode === PaymentMode.SPLIT_EQUALLY && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <SplitEqualControl 
              totalAmount={remainingAmount}
              splitCount={splitCount}
              onCountChange={setSplitCount}
              moneda={moneda}
            />
          </div>
        )}

        {/* Bill Details Card */}
        <section className="bg-white rounded-2xl shadow-sm border border-gray-100/50 overflow-hidden transition-all duration-300">
          <SearchBar value={searchQuery} onChange={setSearchQuery} />

          <div className="divide-y divide-gray-100/50">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  isSelected={selectedItemIds.has(item.id)}
                  mode={paymentMode}
                  onToggle={handleToggleItem}
                  isPaid={isItemPaid(item)}
                  moneda={moneda}
                  taxRate={taxRate}
                  impuestosIncluded={impuestosIncluded}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <p className="text-gray-400 font-medium text-[12px]">No se encontraron items</p>
              </div>
            )}
          </div>
          
          {/* Subtle total at bottom of list */}
          <div className="h-3 bg-gray-50/30" />
        </section>

        {/* Summary Card */}
        {paymentMode !== PaymentMode.SPLIT_EQUALLY && (
          <SummarySection
            subtotal={billSubtotal}
            taxAmount={billTaxAmount}
            total={totalAmount}
            paid={paidAmount}
            remaining={remainingAmount}
            selectedSubtotal={selectedSubtotal}
            selectedTaxAmount={selectedTaxAmount}
            selectedTotal={currentPayableAmount}
            showSelectionTotal={paymentMode === PaymentMode.SELECT_ITEMS}
            moneda={moneda}
            showTaxBreakdown={!impuestosIncluded}
          />
        )}
      </main>

      {/* Premium Floating Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 p-3 pb-4 z-40 pointer-events-none">
        <div className="max-w-md mx-auto pointer-events-auto">
          <button
            onClick={handlePayment}
            disabled={currentPayableAmount <= 0 || isProcessing}
            className={`
              w-full relative h-14 rounded-xl font-semibold shadow-xl shadow-gray-900/15
              flex items-center justify-between px-4 transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)]
              overflow-hidden group
              ${currentPayableAmount > 0 && !isProcessing
                ? 'bg-[#1C1C1E] text-white hover:scale-[1.01] active:scale-[0.98]' 
                : 'bg-[#E5E5EA] text-gray-400 cursor-not-allowed shadow-none'}
            `}
          >
            {/* Background Gradient Shine */}
            <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] ${currentPayableAmount <= 0 ? 'hidden' : ''}`} />

            {isProcessing ? (
              <div className="w-full flex justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2.5">
                  <div className="bg-white/10 p-1 rounded-lg">
                    <Lock size={14} className={currentPayableAmount > 0 ? "text-white" : "text-gray-400"} />
                  </div>
                  <span className="text-[13px] font-medium tracking-wide">
                    Pagar <span className="opacity-90">{paymentMode === PaymentMode.FULL ? 'Cuenta Completa' : 'Monto'}</span>
                  </span>
                </div>
                
                <div className="flex items-center gap-2.5">
                  <span className="text-[15px] font-bold tracking-tight">
                    {formatCurrency(currentPayableAmount, moneda)}
                  </span>
                  <ArrowRight size={16} className="opacity-60 group-hover:translate-x-1 transition-transform" />
                </div>
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Global CSS for shimmer */}
      <style>{`
        @keyframes shimmer {
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  )
}
